/**
 * API Reference documentation page content.
 *
 * AUTO-GENERATED — do not edit manually.
 * Source: fastapi/scripts/generate_api_docs.py
 * Run: pnpm run gen:api-docs
 */

export const apiReferenceContent = `
Base URL: \`https://m8tes.ai/api/v2\`

All requests require an API key in the Authorization header:

\`\`\`
Authorization: Bearer m8_your_key_here
\`\`\`

---

## Teammates

Teammates are reusable agent personas with instructions and tools.

### Create Teammate

\`POST /teammates\`

| Field | Type | Required | Description |
|-------|------|----------|-------------|
| name | string | yes | Name |
| instructions | string | no | Instructions |
| tools | string[] | no | Tool names, e.g. ['gmail', 'slack'] |
| role | string | no | Agent persona role, e.g. 'Customer support specialist'. |
| goals | string | no | Agent goals. Injected into system prompt. |
| user_id | string | no | Your end-user ID for data isolation |
| metadata | object | no | Arbitrary key-value data |
| allowed_senders | string[] | no | Email addresses or @domain patterns allowed to email this teammate. Overrides default. |

\`\`\`bash
curl \\
  -X POST \\
  https://m8tes.ai/api/v2/teammates \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "Support Bot","instructions": "Handle tickets","tools": ["gmail"]}'
\`\`\`

\`\`\`python
client.teammates.create(
    name="Support Bot",
    tools=["gmail"],
    instructions="Handle tickets"
)
\`\`\`

**Response** \`201 Created\`

\`\`\`json
{
  "id": 1,
  "name": "Support Bot",
  "instructions": "Handle tickets",
  "tools": [
    "gmail"
  ],
  "status": "enabled",
  "created_at": "2026-01-15T10:00:00Z"
}
\`\`\`

### List Teammates

\`GET /teammates\`

| Parameter | Type | Description |
|-----------|------|-------------|
| user_id | string | Filter by end-user ID |
| limit | int | Max results (1-100, default 20) |
| starting_after | int | Cursor: last item ID |

\`\`\`bash
curl \\
  https://m8tes.ai/api/v2/teammates \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

\`\`\`python
page = client.teammates.list(user_id="customer_123")
for teammate in page.data:
    print(teammate.name)
\`\`\`

All list endpoints return a paginated envelope:

\`\`\`json
{
  "data": [...],
  "has_more": true
}
\`\`\`

Use \`starting_after\` with the last item's ID to fetch the next page.

### Get Teammate

\`GET /teammates/{teammate_id}\`

\`\`\`bash
curl \\
  https://m8tes.ai/api/v2/teammates/1 \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

\`\`\`python
bot = client.teammates.get(1)
\`\`\`

**Response** \`200 OK\`

\`\`\`json
{
  "id": 1,
  "name": "Support Bot",
  "instructions": "Handle tickets",
  "tools": [
    "gmail"
  ],
  "status": "enabled",
  "created_at": "2026-01-15T10:00:00Z"
}
\`\`\`

### Update Teammate

\`PATCH /teammates/{teammate_id}\`

| Field | Type | Required | Description |
|-------|------|----------|-------------|
| name | string | no | Name |
| instructions | string | no | Instructions |
| tools | string[] | no | Tools |
| role | string | no | Role |
| goals | string | no | Goals |
| metadata | object | no | Metadata |
| allowed_senders | string[] | no | Allowed Senders |

\`\`\`bash
curl \\
  -X PATCH \\
  https://m8tes.ai/api/v2/teammates/1 \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "Renamed Bot","tools": ["gmail","slack"]}'
\`\`\`

\`\`\`python
client.teammates.update(1, name="Renamed Bot", tools=["gmail", "slack"])
\`\`\`

**Response** \`200 OK\`

\`\`\`json
{
  "id": 1,
  "name": "Support Bot",
  "instructions": "Handle tickets",
  "tools": [
    "gmail"
  ],
  "status": "enabled",
  "created_at": "2026-01-15T10:00:00Z"
}
\`\`\`

### Delete Teammate

Soft-deletes (archives) the teammate. Returns \`204 No Content\`.

\`DELETE /teammates/{teammate_id}\`

\`\`\`bash
curl \\
  -X DELETE \\
  https://m8tes.ai/api/v2/teammates/1 \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

\`\`\`python
client.teammates.delete(1)
\`\`\`

### Enable Email Inbox

\`POST /teammates/{teammate_id}/email-inbox\`

\`\`\`bash
curl \\
  -X POST \\
  https://m8tes.ai/api/v2/teammates/1/email-inbox \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

### Disable Email Inbox

\`DELETE /teammates/{teammate_id}/email-inbox\`

\`\`\`bash
curl \\
  -X DELETE \\
  https://m8tes.ai/api/v2/teammates/1/email-inbox \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

---

## Runs

Runs are task executions. They stream Server-Sent Events by default.

### Create Run

\`POST /runs\`

| Field | Type | Required | Description |
|-------|------|----------|-------------|
| teammate_id | int | no | Teammate Id |
| message | string | yes | Message |
| tools | string[] | no | Tools |
| stream | bool | no | Stream |
| name | string | no | Teammate name (creates new if no teammate_id) |
| instructions | string | no | Teammate instructions |
| user_id | string | no | Your end-user ID for data isolation |
| metadata | object | no | Arbitrary key-value data |
| memory | bool | no | Include saved per-user memories in agent context |
| history | bool | no | Include previous run results in agent context |
| permission_mode | string | no | autonomous=auto-approve all tools, approval=pause and ask before tool use, plan=require plan approval before execution |

\`\`\`bash
curl \\
  -X POST \\
  https://m8tes.ai/api/v2/runs \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"teammate_id": 1,"message": "Close open tickets","stream": false}'
\`\`\`

\`\`\`python
# Streaming (default)
for event in client.runs.create(teammate_id=1, message="Close open tickets"):
    print(event.type, event.raw)

# Non-streaming
run = client.runs.create(teammate_id=1, message="Close open tickets", stream=False)
\`\`\`

**Streaming response** — Server-Sent Events:

\`\`\`
data: {"type": "text-delta", "delta": "Hello"}
data: {"type": "tool-call-start", "toolName": "gmail_send", "toolCallId": "tc_1"}
data: {"type": "tool-result-end", "toolCallId": "tc_1", "result": "..."}
data: {"type": "done", "stop_reason": "end_turn"}
\`\`\`

**Non-streaming response** \`200 OK\`:

\`\`\`json
{
  "id": 42,
  "teammate_id": 1,
  "status": "running",
  "output": null,
  "user_id": "customer_123",
  "metadata": null,
  "created_at": "2026-01-15T10:05:00Z"
}
\`\`\`

> **Polling:** Non-streaming runs return immediately with \`status: "running"\`. Poll \`GET /runs/{id}\` every 2 seconds until \`status\` is \`completed\`, \`failed\`, or \`cancelled\`.

**Quick start** — omit \`teammate_id\` and provide \`name\` to auto-create a teammate:

\`\`\`python
for event in client.runs.create(
    message="Draft release notes",
    name="Release Bot",
    instructions="Write concise release notes"
):
    print(event.type, event.raw)
\`\`\`

### List Runs

\`GET /runs\`

| Parameter | Type | Description |
|-----------|------|-------------|
| teammate_id | int | Filter by teammate |
| user_id | string | Filter by end-user ID |
| status | string | Filter by status (\`running\`, \`completed\`, \`failed\`, \`cancelled\`) |
| limit | int | Max results (1-100, default 20) |
| starting_after | int | Cursor: last item ID |

\`\`\`bash
curl \\
  https://m8tes.ai/api/v2/runs \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

\`\`\`python
page = client.runs.list(teammate_id=1)
for run in page.data:
    print(run.status)
\`\`\`

### Get Run

\`GET /runs/{run_id}\`

\`\`\`bash
curl \\
  https://m8tes.ai/api/v2/runs/42 \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

\`\`\`python
run = client.runs.get(42)
\`\`\`

**Response** \`200 OK\`

\`\`\`json
{
  "id": 42,
  "teammate_id": 1,
  "status": "running",
  "user_id": "customer_123",
  "created_at": "2026-01-15T10:05:00Z"
}
\`\`\`

### Approve Permission

\`POST /runs/{run_id}/approve\`

| Field | Type | Required | Description |
|-------|------|----------|-------------|
| request_id | string | yes | Permission request ID |
| decision | string | yes | Decision |
| remember | bool | no | Remember this decision for future requests |

\`\`\`bash
curl \\
  -X POST \\
  https://m8tes.ai/api/v2/runs/42/approve \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"request_id": "req_abc123","decision": "allow","remember": true}'
\`\`\`

**Response** \`200 OK\`

\`\`\`json
{
  "request_id": "req_abc123",
  "tool_name": "gmail_send",
  "tool_input": {
    "to": "user@example.com"
  },
  "status": "pending",
  "created_at": "2026-01-15T10:05:30Z"
}
\`\`\`

### Cancel Run

Cancel an active run. Returns \`409\` if the run is already completed, failed, or cancelled.

\`POST /runs/{run_id}/cancel\`

\`\`\`bash
curl \\
  -X POST \\
  https://m8tes.ai/api/v2/runs/42/cancel \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

\`\`\`python
client.runs.cancel(42)
\`\`\`

**Response** \`200 OK\`

\`\`\`json
{
  "id": 42,
  "teammate_id": 1,
  "status": "running",
  "user_id": "customer_123",
  "created_at": "2026-01-15T10:05:00Z"
}
\`\`\`

### Reply To Run

Send a follow-up message on an existing run to continue the conversation.

\`POST /runs/{run_id}/reply\`

| Field | Type | Required | Description |
|-------|------|----------|-------------|
| message | string | yes | Message |
| stream | bool | no | Stream |

\`\`\`bash
curl \\
  -X POST \\
  https://m8tes.ai/api/v2/runs/42/reply \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"message": "What about VIP tickets?","stream": false}'
\`\`\`

\`\`\`python
for event in client.runs.reply(run_id=42, message="What about VIP tickets?"):
    print(event.type, event.raw)
\`\`\`

### List Run Permissions

Returns pending and resolved tool permission requests for runs using \`approval\` or \`plan\` mode.

\`GET /runs/{run_id}/permissions\`

\`\`\`bash
curl \\
  https://m8tes.ai/api/v2/runs/42/permissions \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

---

## Tasks

Tasks are reusable job definitions. Create a task, then attach triggers via \`tasks.triggers.create()\`.

### Create Task

\`POST /tasks\`

| Field | Type | Required | Description |
|-------|------|----------|-------------|
| teammate_id | int | yes | Teammate Id |
| name | string | no | Name |
| instructions | string | yes | Instructions |
| tools | string[] | no | Tools |
| expected_output | string | no | Description of expected output format. |
| goals | string | no | Task-specific goals. |
| user_id | string | no | Your end-user ID for data isolation |

\`\`\`bash
curl \\
  -X POST \\
  https://m8tes.ai/api/v2/tasks \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"teammate_id": 1,"name": "Ticket Closer","instructions": "Close open support tickets"}'
\`\`\`

\`\`\`python
task = client.tasks.create(
    teammate_id=1,
    instructions="Close open support tickets",
    name="Ticket Closer"
)
\`\`\`

**Response** \`201 Created\`

\`\`\`json
{
  "id": 10,
  "teammate_id": 1,
  "name": "Ticket Closer",
  "instructions": "Close open support tickets",
  "tools": [],
  "status": "enabled",
  "created_at": "2026-01-15T10:00:00Z"
}
\`\`\`

### List Tasks

\`GET /tasks\`

| Parameter | Type | Description |
|-----------|------|-------------|
| teammate_id | int | Filter by teammate |
| user_id | string | Filter by end-user ID |
| limit | int | Max results (1-100, default 20) |
| starting_after | int | Cursor: last item ID |

\`\`\`bash
curl \\
  https://m8tes.ai/api/v2/tasks \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

\`\`\`python
page = client.tasks.list(teammate_id=1)
\`\`\`

### Get Task

\`GET /tasks/{task_id}\`

\`\`\`bash
curl \\
  https://m8tes.ai/api/v2/tasks/10 \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

\`\`\`python
task = client.tasks.get(10)
\`\`\`

**Response** \`200 OK\`

\`\`\`json
{
  "id": 10,
  "teammate_id": 1,
  "name": "Ticket Closer",
  "instructions": "Close open support tickets",
  "tools": [],
  "status": "enabled",
  "created_at": "2026-01-15T10:00:00Z"
}
\`\`\`

### Update Task

\`PATCH /tasks/{task_id}\`

| Field | Type | Required | Description |
|-------|------|----------|-------------|
| name | string | no | Name |
| instructions | string | no | Instructions |
| tools | string[] | no | Tools |
| expected_output | string | no | Expected Output |
| goals | string | no | Goals |

\`\`\`bash
curl \\
  -X PATCH \\
  https://m8tes.ai/api/v2/tasks/10 \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"instructions": "Updated instructions"}'
\`\`\`

\`\`\`python
client.tasks.update(10, instructions="Updated instructions")
\`\`\`

**Response** \`200 OK\`

\`\`\`json
{
  "id": 10,
  "teammate_id": 1,
  "name": "Ticket Closer",
  "instructions": "Close open support tickets",
  "tools": [],
  "status": "enabled",
  "created_at": "2026-01-15T10:00:00Z"
}
\`\`\`

### Delete Task

\`DELETE /tasks/{task_id}\`

\`\`\`bash
curl \\
  -X DELETE \\
  https://m8tes.ai/api/v2/tasks/10 \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

\`\`\`python
client.tasks.delete(10)
\`\`\`

### Run Task

\`POST /tasks/{task_id}/runs\`

| Field | Type | Required | Description |
|-------|------|----------|-------------|
| stream | bool | no | Stream |
| user_id | string | no | Your end-user ID for data isolation |
| metadata | object | no | Arbitrary key-value data |
| memory | bool | no | Include saved per-user memories in agent context |
| history | bool | no | Include previous run results in agent context |
| permission_mode | string | no | autonomous=auto-approve, approval=pause before tools, plan=require plan approval |

\`\`\`bash
curl \\
  -X POST \\
  https://m8tes.ai/api/v2/tasks/10/runs \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"stream": false}'
\`\`\`

\`\`\`python
# Streaming (default)
for event in client.tasks.run(task_id=10):
    print(event.type, event.raw)

# Non-streaming
run = client.tasks.run(task_id=10, stream=False)
\`\`\`

### Create Trigger

\`POST /tasks/{task_id}/triggers\`

| Field | Type | Required | Description |
|-------|------|----------|-------------|
| type | string | yes | Type |
| cron | string | no | Cron |
| interval_seconds | int | no | Interval Seconds |
| timezone | string | no | Timezone |

\`\`\`bash
curl \\
  -X POST \\
  https://m8tes.ai/api/v2/tasks/10/triggers \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"type": "schedule","cron": "0 9 * * 1","timezone": "America/New_York"}'
\`\`\`

\`\`\`python
trigger = client.tasks.triggers.create(
    task_id=10,
    type="schedule",
    cron="0 9 * * 1",
    timezone="America/New_York"
)
\`\`\`

**Response** \`201 Created\`

\`\`\`json
{
  "id": 5,
  "type": "schedule",
  "enabled": true,
  "cron": "0 9 * * 1",
  "timezone": "America/New_York"
}
\`\`\`

### List Triggers

\`GET /tasks/{task_id}/triggers\`

\`\`\`bash
curl \\
  https://m8tes.ai/api/v2/tasks/10/triggers \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

\`\`\`python
triggers = client.tasks.triggers.list(10)
\`\`\`

### Delete Trigger

\`DELETE /tasks/{task_id}/triggers/{trigger_id}\`

\`\`\`bash
curl \\
  -X DELETE \\
  https://m8tes.ai/api/v2/tasks/10/triggers/5 \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

\`\`\`python
client.tasks.triggers.delete(task_id=10, trigger_id=5)
\`\`\`

---

## Apps

List available tools, connect integrations via OAuth, and manage end-user connections.

### List Apps

Pass \`user_id\` to check connection status for a specific end-user.

\`GET /apps\`

| Parameter | Type | Description |
|-----------|------|-------------|
| user_id | string | End-user ID to check connections for |

\`\`\`bash
curl \\
  https://m8tes.ai/api/v2/apps \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

\`\`\`python
apps = client.apps.list()
apps = client.apps.list(user_id="cust_123")
\`\`\`

### Connect App

\`POST /apps/{app_name}/connect\`

| Field | Type | Required | Description |
|-------|------|----------|-------------|
| redirect_uri | string | yes | URL to redirect after OAuth |
| user_id | string | no | End-user ID for multi-tenant connections |

\`\`\`bash
curl \\
  -X POST \\
  https://m8tes.ai/api/v2/apps/gmail/connect \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"redirect_uri": "https://yourapp.com/callback","user_id": "cust_123"}'
\`\`\`

\`\`\`python
conn = client.apps.connect("gmail", "https://yourapp.com/callback", user_id="cust_123")
print(conn.authorization_url)  # Redirect your user here
print(conn.connection_id)      # Save for the next step
\`\`\`

**Response** \`200 OK\`

\`\`\`json
{
  "authorization_url": "https://accounts.google.com/o/oauth2/auth?...",
  "connection_id": "conn_abc123"
}
\`\`\`

### Disconnect App

\`DELETE /apps/{app_name}/connections\`

| Parameter | Type | Description |
|-----------|------|-------------|
| user_id | string | End-user ID to disconnect |

\`\`\`bash
curl \\
  -X DELETE \\
  https://m8tes.ai/api/v2/apps/gmail/connections \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

\`\`\`python
client.apps.disconnect("gmail", user_id="cust_123")
\`\`\`

### Connect App Complete

After the user completes OAuth and is redirected back:

\`POST /apps/{app_name}/connect/complete\`

| Field | Type | Required | Description |
|-------|------|----------|-------------|
| connection_id | string | yes | Connection Id |
| user_id | string | no | End-user ID for multi-tenant connections |

\`\`\`bash
curl \\
  -X POST \\
  https://m8tes.ai/api/v2/apps/gmail/connect/complete \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"connection_id": "conn_abc123","user_id": "cust_123"}'
\`\`\`

\`\`\`python
result = client.apps.connect_complete("gmail", "conn_abc123", user_id="cust_123")
print(result.status)  # "connected"
\`\`\`

**Response** \`200 OK\`

\`\`\`json
{
  "status": "connected",
  "app": "gmail"
}
\`\`\`

---

## Memories

Pre-populate or manage end-user memories. Memories are injected into the agent's context during execution to personalize responses.

### Create Memory

\`POST /memories\`

| Field | Type | Required | Description |
|-------|------|----------|-------------|
| user_id | string | yes | End-user ID this memory belongs to |
| content | string | yes | Content |

\`\`\`bash
curl \\
  -X POST \\
  https://m8tes.ai/api/v2/memories \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"user_id": "customer_123","content": "Prefers email over Slack"}'
\`\`\`

\`\`\`python
memory = client.memories.create(user_id="customer_123", content="Prefers email over Slack")
\`\`\`

**Response** \`201 Created\`

\`\`\`json
{
  "id": 7,
  "user_id": "customer_123",
  "content": "Prefers email over Slack",
  "source": "api",
  "created_at": "2026-01-15T10:00:00Z"
}
\`\`\`

### List Memories

The \`user_id\` query parameter is required.

\`GET /memories\`

| Parameter | Type | Description |
|-----------|------|-------------|
| user_id | string | End-user ID to list memories for |
| limit | int | Max results (1-100, default 20) |
| starting_after | int | Cursor: last item ID |

\`\`\`bash
curl \\
  https://m8tes.ai/api/v2/memories?user_id=customer_123 \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

\`\`\`python
memories = client.memories.list(user_id="customer_123")
\`\`\`

### Delete Memory

The \`user_id\` query parameter is required to prevent accidental deletion of account-level memories.

\`DELETE /memories/{memory_id}\`

| Parameter | Type | Description |
|-----------|------|-------------|
| user_id | string | End-user ID (prevents accidental deletion of account-level memories) |

\`\`\`bash
curl \\
  -X DELETE \\
  https://m8tes.ai/api/v2/memories/7?user_id=customer_123 \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

\`\`\`python
client.memories.delete(7, user_id="customer_123")
\`\`\`

---

## Permissions

Pre-configure tool allow-lists for your end-users. Tools added here are auto-approved when runs use \`approval\` mode.

### Create Permission

Idempotent — re-creating the same permission returns the existing record.

\`POST /permissions\`

| Field | Type | Required | Description |
|-------|------|----------|-------------|
| user_id | string | yes | End-user ID |
| tool | string | yes | Tool name to allow, e.g. 'gmail' |

\`\`\`bash
curl \\
  -X POST \\
  https://m8tes.ai/api/v2/permissions \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"user_id": "customer_123","tool": "gmail"}'
\`\`\`

\`\`\`python
policy = client.permissions.create(user_id="customer_123", tool="gmail")
\`\`\`

**Response** \`201 Created\`

\`\`\`json
{
  "id": 3,
  "user_id": "customer_123",
  "tool_name": "gmail",
  "created_at": "2026-01-15T10:00:00Z"
}
\`\`\`

### List Permissions

\`GET /permissions\`

| Parameter | Type | Description |
|-----------|------|-------------|
| user_id | string | End-user ID to list permissions for |
| limit | int | Max results (1-100, default 20) |
| starting_after | int | Cursor: last item ID |

\`\`\`bash
curl \\
  https://m8tes.ai/api/v2/permissions?user_id=customer_123 \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

\`\`\`python
policies = client.permissions.list(user_id="customer_123")
\`\`\`

### Delete Permission

\`DELETE /permissions/{permission_id}\`

| Parameter | Type | Description |
|-----------|------|-------------|
| user_id | string | End-user ID (prevents accidental cross-user deletion) |

\`\`\`bash
curl \\
  -X DELETE \\
  https://m8tes.ai/api/v2/permissions/3?user_id=customer_123 \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

\`\`\`python
client.permissions.delete(3, user_id="customer_123")
\`\`\`

---

## Webhooks

Register URLs to receive signed event notifications when runs change status.

### Create Webhook

\`POST /webhooks\`

| Field | Type | Required | Description |
|-------|------|----------|-------------|
| url | string | yes | HTTPS URL to receive events |
| events | string[] | no | Events: run.started, run.completed, run.failed, run.awaiting_input |

\`\`\`bash
curl \\
  -X POST \\
  https://m8tes.ai/api/v2/webhooks \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"url": "https://your-app.com/webhooks/m8tes","events": ["run.completed"]}'
\`\`\`

\`\`\`python
webhook = client.webhooks.create(
    url="https://your-app.com/webhooks/m8tes",
    events=["run.completed"]
)
print(webhook.secret)  # Save this — only shown on creation
\`\`\`

> **Note:** The \`secret\` is only returned on creation. Store it securely.

**Response** \`201 Created\`

\`\`\`json
{
  "id": 2,
  "url": "https://your-app.com/webhooks/m8tes",
  "events": [
    "run.completed"
  ],
  "secret": "a1b2c3d4e5f6...",
  "active": true,
  "delivery_status": "active",
  "created_at": "2026-01-15T10:00:00Z"
}
\`\`\`

### List Webhooks

Secrets are masked in list responses.

\`GET /webhooks\`

| Parameter | Type | Description |
|-----------|------|-------------|
| limit | int | Max results (1-100, default 20) |
| starting_after | int | Cursor: last item ID |

\`\`\`bash
curl \\
  https://m8tes.ai/api/v2/webhooks \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

\`\`\`python
webhooks = client.webhooks.list()
\`\`\`

### Get Webhook

\`GET /webhooks/{webhook_id}\`

\`\`\`bash
curl \\
  https://m8tes.ai/api/v2/webhooks/2 \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

\`\`\`python
webhook = client.webhooks.get(2)
\`\`\`

**Response** \`200 OK\`

\`\`\`json
{
  "id": 2,
  "url": "https://your-app.com/webhooks/m8tes",
  "events": [
    "run.completed"
  ],
  "secret": "a1b2c3d4e5f6...",
  "active": true,
  "delivery_status": "active",
  "created_at": "2026-01-15T10:00:00Z"
}
\`\`\`

### Update Webhook

\`PATCH /webhooks/{webhook_id}\`

| Field | Type | Required | Description |
|-------|------|----------|-------------|
| url | string | no | Url |
| events | string[] | no | Events |
| active | bool | no | Active |
| rotate_secret | bool | no | Generate a new signing secret |

\`\`\`bash
curl \\
  -X PATCH \\
  https://m8tes.ai/api/v2/webhooks/2 \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"events": ["run.started","run.completed"]}'
\`\`\`

\`\`\`python
client.webhooks.update(2, events=["run.started", "run.completed"])
\`\`\`

**Response** \`200 OK\`

\`\`\`json
{
  "id": 2,
  "url": "https://your-app.com/webhooks/m8tes",
  "events": [
    "run.completed"
  ],
  "secret": "a1b2c3d4e5f6...",
  "active": true,
  "delivery_status": "active",
  "created_at": "2026-01-15T10:00:00Z"
}
\`\`\`

### Delete Webhook

\`DELETE /webhooks/{webhook_id}\`

\`\`\`bash
curl \\
  -X DELETE \\
  https://m8tes.ai/api/v2/webhooks/2 \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

\`\`\`python
client.webhooks.delete(2)
\`\`\`

### List Deliveries

View delivery attempts for a webhook endpoint.

\`GET /webhooks/{webhook_id}/deliveries\`

| Parameter | Type | Description |
|-----------|------|-------------|
| limit | int | Max results (1-100, default 20) |
| starting_after | int | Cursor: last delivery ID |

\`\`\`bash
curl \\
  https://m8tes.ai/api/v2/webhooks/2/deliveries \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

\`\`\`python
deliveries = client.webhooks.list_deliveries(2)
for d in deliveries.data:
    print(d.event_type, d.status, d.attempts)
\`\`\`

---

## Errors

All errors follow a standard format:

\`\`\`json
{
  "error": {
    "type": "not_found",
    "message": "Teammate not found",
    "code": 404,
    "request_id": "req_abc123"
  }
}
\`\`\`

| Status | Type | SDK Exception | Description |
|--------|------|---------------|-------------|
| 400 | invalid_request_error | APIError | Invalid request |
| 401 | authentication_error | AuthenticationError | Invalid or missing API key |
| 404 | not_found | NotFoundError | Resource not found |
| 409 | conflict_error | APIError | Resource conflict (duplicate, not active) |
| 422 | validation_error | ValidationError | Invalid request parameters |
| 429 | rate_limit_error | RateLimitError | Too many requests (auto-retried by SDK) |
| 500+ | api_error | APIError | Server error (auto-retried by SDK) |
`;
